/**
 * Media capabilities of a peer of a robot.
 *
 * Terminology: a robot is reachable through several peers (conn-manager calls them "robot
 * clients"); each peer registers its own `setting.cameras / microphones / speakers`
 * (ketirtc get_media_configs, every entry has a `class_name`). A `peerInfo` is that
 * registered document: { id, robot_id, setting: {...}, status, ... }.
 *
 * A `DummyCamera` is the placeholder a peer runs when it has no real camera, so it never
 * counts as a camera — the same rule the user-data reducers and providers apply
 * (shared/reducer/userDataReducer.js hasCamera / hasSpeaker / hasMicrophones).
 * The grouped robot data additionally carries flat `has_camera / has_microphones /
 * has_speaker` flags computed with this rule; they are honoured as a fallback for peers
 * that only advertise a capability that way.
 */

export const DUMMY_CAMERA_CLASS_NAME = "DummyCamera";

/** Real cameras of the peer (DummyCamera entries removed). */
export const getPeerCameras = (peerInfo) =>
  (peerInfo?.setting?.cameras || []).filter(
    (camera) => camera?.class_name !== DUMMY_CAMERA_CLASS_NAME
  );

export const getPeerMicrophones = (peerInfo) => peerInfo?.setting?.microphones || [];

export const getPeerSpeakers = (peerInfo) => peerInfo?.setting?.speakers || [];

export const peerHasCamera = (peerInfo) =>
  getPeerCameras(peerInfo).length > 0 || !!peerInfo?.has_camera;

export const peerHasMicrophones = (peerInfo) =>
  getPeerMicrophones(peerInfo).length > 0 || !!peerInfo?.has_microphones;

export const peerHasSpeaker = (peerInfo) =>
  getPeerSpeakers(peerInfo).length > 0 || !!peerInfo?.has_speaker;

/** The peer can send (microphone) or receive (speaker) audio. */
export const peerHasAudio = (peerInfo) => peerHasMicrophones(peerInfo) || peerHasSpeaker(peerInfo);
